import React, { useState, useContext, useRef } from "react";
import { PhotoManagerContext } from "..";
import { Folder, CreateFolderModalProps, EnhancedFolder } from "../types";
import toastr from "../../../common/toastr";
import {
  validateName,
  restrictedTermsError
} from "../../../../utils/validateName";
import { createLibraryFolder } from "../../API/api";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";
import {
  decodeEntityPath,
  getEncodedEntityPath,
  getEntitySegment
} from "../FolderFeatures/EncryptionOfFolder";

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  type,
  onClose,
  parentFolderId,
  forceRootLevel = false
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);

  usePopupClickOutside(popupRef, () => onClose());

  const onCreateLibraryFolderSuccess = (newLibraryFolder: Folder) => {
    if (!context) return;

    const newFolderId = newLibraryFolder.id;
    const folderName = newLibraryFolder.name;

    const newFolder: EnhancedFolder = {
      ...newLibraryFolder,
      children: [],
      events: []
    };

    const newSegment = getEntitySegment({
      id: newFolderId,
      referenceId: null,
      type: "LibraryFolder",
      isReference: false
    });

    // If forceRootLevel is true (header dropdown), always create at root level
    let newEncodedPath: string;
    if (forceRootLevel) {
      newEncodedPath = getEncodedEntityPath(newSegment);
    } else {
      const currentEncoded = context.encodedString || null;
      const decodedPath = currentEncoded
        ? decodeEntityPath(currentEncoded)
        : "";
      const updatedDecodedPath = decodedPath
        ? `${decodedPath}/${newSegment}`
        : newSegment;
      newEncodedPath = getEncodedEntityPath(updatedDecodedPath);
    }

    context.setEncodedString(newEncodedPath);
    context.setFolderData({ id: newFolderId, name: folderName });
    context.setSelectedView("empty-folder");
    context.setFolderId(newFolderId);

    context.addNewFolderToTree?.(newFolder);

    context.handleFolderClick?.(newFolder, newEncodedPath);
  };

  const handleCreateFolder = async () => {
    const isValidName = validateName(name);
    if (!isValidName) {
      toastr.error(restrictedTermsError);
      return;
    } else if (!name.trim()) {
      toastr.error("Please enter a valid name");
      return;
    }

    setLoading(true);
    try {
      if (type === "library-folder") {
        await createLibraryFolder(
          { library_folder: { name: name, parent_folder_id: null } },
          onCreateLibraryFolderSuccess
        );
      } else {
        await createLibraryFolder(
          { library_folder: { name: name, parent_folder_id: parentFolderId } },
          onCreateLibraryFolderSuccess
        );
      }
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCreateFolder();
    }
  };

  return (
    <div>
      <div id="filter-dialog" className="modal fade in geo-modal" role="dialog">
        <div
          className="modal-dialog filter-dialog-content create-folder-modal"
          ref={popupRef}
        >
          <div className="geo-modal__header">
            <div className="create-folder-header">Create a Folder</div>
          </div>
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="modal-content create-modal-content-container">
            <p className="create-modal-content">
              Events can be grouped inside folders, you can also add shortcuts
              to events, and other folders as well.
            </p>
            <div className="geo-form geo-form--fw folder-name-input">
              <input
                id="name-input"
                type="text"
                className="geo-form__input ws-geo-input"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Folder name`}
                autoFocus
                disabled={loading}
              />
            </div>
            <div className="modal-footer geo-modal__actions dont-break ws-delete-footer-btns">
              <button
                className="geo-btn geo-btn--secondary geo-btn--caps create-folder-btn"
                onClick={handleCreateFolder}
                disabled={loading || !name.trim()}
              >
                {loading ? (
                  <div className="loading-spinner">
                    Creating... <span className="fa fa-spinner fa-spin"></span>
                  </div>
                ) : (
                  "CREATE"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </div>
  );
};

export default CreateFolderModal;
